import React, { useState } from 'react';
import GlassContainer from 'components/GlassContainer/GlassContainer';
import 'components/GlassContainer/GlassContainer.css';
import { Table, TableColumn } from './Table';
import { Points } from 'components/Points/Points';
import PriceFilter from 'components/PriceFilter/PriceFilter';
import 'components/PriceFilter/PriceFilter.css';
import FiltersBar from 'components/FiltersBar/FiltersBar';
import CardGroupHeader from 'components/CardGroupContainer/components/CardGroupHeader';
import CardGroupContainer from 'components/CardGroupContainer/CardGroupContainer';
import CardPrintingsHeader from 'components/CardPrintingsHeader/CardPrintingsHeader';
import CardPrintingsContainer from 'components/CardPrintingsContainer/CardPrintingsContainer';
import ManaFilters from 'components/ManaFilters/ManaFilters';
import { Dropdown } from 'components/Dropdown/Dropdown';
import 'components/Dropdown/Dropdown.css';
import { AddCards } from 'screens/Main/tabs/MyCards/components/MyCardsCollection/components/AddCards/AddCards';
import QuickSearchBar from 'screens/Main/Search/Components/QuickSearchBar';
import HoverOverlay from 'screens/Main/Search/Components/HoverOverlay';
import { AssetUrls } from 'config/assets';

interface CardPrint {
  name: string;
  points: number;
  image: string;
}

const dummyData: CardPrint[] = [
  { name: 'Final Fantasy #527', points: 49590, image: AssetUrls.viewCard.sephirothFabled },
  { name: 'Final Fantasy Promos', points: 10550, image: AssetUrls.viewCard.sephirothFabled },
  { name: 'Final Fantasy #317', points: 7919, image: AssetUrls.viewCard.sephirothFabled },
];

export default function ComponentPlayground() {
  const [foilValue, setFoilValue] = useState('Foil');
  const [languageValue, setLanguageValue] = useState('English'); // default is English

  const columns: TableColumn<CardPrint>[] = [
    {
      label: 'Print',
      key: 'name',
      render: (row: CardPrint) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <img src={row.image} alt={row.name} style={{ width: '32px', height: '32px', borderRadius: '4px' }} />
          {row.name}
        </div>
      ),
    },
    { label: 'Points', key: 'points', render: (row: CardPrint) => <Points points={row.points} type="small" /> },
  ];

  return (
    <div className="min-h-screen bg-zinc-900 text-white p-8">
      <h1 className="text-2xl font-bold mb-6">Component Playground</h1>

      {/* Dropdown Test */}
      <div style={{ margin: '20px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ maxWidth: '240px' }}>
          <h3 style={{ color: '#fff', marginBottom: '8px' }}>Foil Status</h3>
          <Dropdown
            label=""
            options={['Foil', 'Non-Foil', 'Any']}
            value={foilValue}
            onChange={(newValue) => setFoilValue(newValue)}
          />
        </div>

        <div style={{ maxWidth: '240px' }}>
          <h3 style={{ color: '#fff', marginBottom: '8px' }}>Language</h3>
          <Dropdown
            label=""
            options={['English', 'Japanese', 'French', 'German']}
            value={languageValue}
            onChange={(newValue) => setLanguageValue(newValue)}
          />
        </div>
      </div>

      {/* GlassContainer Test */}
      <GlassContainer>
        <p>This is inside a glass container</p>
      </GlassContainer>

      {/* Quick Search Bar | HaveList Test */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Quick Search Bar Test</h3>
        <AddCards
          placeholder="Search for cards..."
          onCardAdd={(cardId) => console.log('Card added with ID:', cardId)}
        />
      </div>

      {/* Quick Search Bar | Search Test */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Quick Search Bar Test</h3>
        <QuickSearchBar 
          placeholder="Search for Magic Cards..."
          bgImageUrl={AssetUrls.search.quickSearchBg}
          onSearchChange={(value) => console.log('Search term:', value)}
        />
      </div>

      {/* Hover Overlay Test */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Hover Overlay Test</h3>
        <HoverOverlay
          title="Territorial Hellkite"
          cardId="123"
          oracleId="test-oracle-id"
          setName="Test Set"
          setCode="TEST"
          nonFoilPrice={63485}
          foilPrice={79117}
        />
      </div>

      {/* Price Filter */}
      <div style={{ padding: '20px', background: '#222', borderRadius: '8px' }}>
        <h3 style={{ color: '#fff' }}>Price Filter Test</h3>
        <PriceFilter defaultMin={4353} defaultMax={54999} onChange={(min, max) => console.log('Price range changed:', min, max)} />
      </div>

      {/* Filters Bar */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Filters Bar Test</h3>
        <FiltersBar onFiltersChange={(filters) => console.log('Filters changed:', filters)} />
      </div>

      {/* Card Group Header */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Card Group Header Test</h3>
        <CardGroupHeader title="16 versions available" lowPrice={4328} highPrice={54999} />
      </div>

      {/* Card Group Container */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Card Group Container Test</h3>
        <CardGroupContainer
          title="16 versions available"
          lowPrice={4328}
          highPrice={54999}
          artistName="John Avon"
          onRemoveAll={() => console.log('Remove all clicked')}
        />
      </div>

      {/* Card Printings Header */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Card Printings Header Test</h3>
        <CardPrintingsHeader
          selectedCount={4}
          onAddToWantlist={() => console.log('Add to Wantlist clicked')}
        />
      </div>
      
      {/* Card Printings Header + Container */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Card Printings Header & Container Test</h3>
        <CardPrintingsHeader
          selectedCount={4}
          onAddToWantlist={() => console.log('Add to Wantlist clicked')}
        />
        <CardPrintingsContainer>
          <p style={{ color: '#fff' }}>Mock content inside the printings container</p>
        </CardPrintingsContainer>
      </div>

      {/* Mana Filters (checkboxes) */}
      <div style={{ margin: '20px 0' }}>
        <h3 style={{ color: '#fff' }}>Mana Filters Test</h3>
        <ManaFilters />
      </div>

      {/* Reusable Table Example */}
      <Table columns={columns} rows={dummyData} />
    </div>
  );
}
